"use client";

/**
 * Overlaid 52-week trend chart for the Compare page.
 *
 * One line per UNIQUE district in the comparison. If two listings
 * sit in the same district, only one line is drawn - doubling it
 * adds visual noise without a new data point. Each line is colored
 * to the EARLIEST listing in that district so the map of
 * "listing → color" stays consistent with the cards / SHAP panel
 * / table dots.
 *
 * Data comes from `/api/trends?level=district&key=<district>` - same
 * endpoint the standalone TrendChart uses. We parallelize one request
 * per unique district and render them together on a single
 * ApexCharts chart with multiple series.
 *
 * Empty-state safety: if no district in the comparison returns
 * enough trend history, we drop the whole chart rather than showing
 * an empty axis - reduces visual noise on new / niche districts.
 */
import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useT, useLocale } from "@/lib/locale-context";
import { useTheme } from "@/lib/theme-context";
import { formatMonthDayShort, formatDateLong } from "@/lib/blog-format";
import { fetchAPI } from "@/lib/api";
import type { AnalysisResult } from "@/types";
import { colorForIndex, type ListingPalette } from "./palette";

const Chart = dynamic(() => import("react-apexcharts"), { ssr: false });

interface TrendData {
  week: string;
  median_price: number | null;
  median_ppm: number | null;
}

interface Props {
  results: AnalysisResult[];
  mode?: "sale" | "rent";
  weeks?: number;
}

/** ISO "2026-W15" → UTC ms for ApexCharts datetime axis. */
function weekToDate(weekKey: string): number {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!m) return Date.now();
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  const jan4 = Date.UTC(year, 0, 4);
  const jan4Dow = new Date(jan4).getUTCDay() || 7;
  const isoWeek1Monday = jan4 - (jan4Dow - 1) * 86400000;
  return isoWeek1Monday + (week - 1) * 7 * 86400000;
}

interface DistrictSeries {
  district: string;
  palette: ListingPalette;
  // Which listings' colors map to this district (for the legend
  // tooltip). One district may host multiple listings; we show the
  // earliest in the listing order.
  listingIndices: number[];
  points: { x: number; y: number }[];
}

export default function OverlaidTrendChart({
  results,
  mode = "sale",
  weeks = 52,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const { resolved } = useTheme();
  const isDark = resolved === "dark";

  // Build the (district → earliest-listing-index) map. Preserves the
  // listing→color binding for shared districts: if listing 1 and 3
  // are both in "Yasamal", the line uses listing 1's indigo. The
  // tooltip still calls out both listing numbers.
  const uniqueDistricts = useMemo(() => {
    const seen = new Map<string, { firstIndex: number; indices: number[] }>();
    results.forEach((r, i) => {
      const loc = (r.location_name || "").trim();
      if (!loc) return;
      const entry = seen.get(loc);
      if (entry) {
        entry.indices.push(i);
      } else {
        seen.set(loc, { firstIndex: i, indices: [i] });
      }
    });
    return Array.from(seen.entries()).map(([district, v]) => ({
      district,
      firstIndex: v.firstIndex,
      indices: v.indices,
    }));
  }, [results]);

  const [seriesData, setSeriesData] = useState<DistrictSeries[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (uniqueDistricts.length === 0) {
      setSeriesData([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled(
      uniqueDistricts.map((d) =>
        fetchAPI<{ data?: TrendData[] }>("/trends", {
          level: "district",
          key: d.district,
          mode,
          rooms: "0",
          weeks: String(weeks),
        }),
      ),
    )
      .then((settled) => {
        if (cancelled) return;
        const built: DistrictSeries[] = [];
        uniqueDistricts.forEach((d, i) => {
          const result = settled[i];
          if (result.status !== "fulfilled") return;
          const raw = result.value?.data || [];
          // Drop null medians so categories/values stay aligned -
          // same approach as the single-listing TrendChart.
          const points = raw
            .map((row) => {
              const v = row.median_ppm;
              return v && v > 0 ? { x: weekToDate(row.week), y: v } : null;
            })
            .filter((p): p is { x: number; y: number } => p !== null);
          if (points.length === 0) return;
          built.push({
            district: d.district,
            palette: colorForIndex(d.firstIndex),
            listingIndices: d.indices,
            points,
          });
        });
        setSeriesData(built);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uniqueDistricts, mode, weeks]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="h-4 w-48 bg-gray-100 rounded animate-pulse mb-4" />
        <div className="h-56 bg-gray-50 rounded-xl animate-pulse" />
      </div>
    );
  }

  // Hide the whole section when we have nothing useful to chart -
  // a blank card with a generic "no data" message adds noise without
  // adding signal. The cards + SHAP + table already tell the story
  // for niche districts without weekly history.
  if (seriesData.length === 0) return null;

  const gridColor = isDark ? "#374151" : "#f3f4f6";
  const labelColor = isDark ? "#9ca3af" : "#9ca3af";

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: "line",
      height: 280,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "system-ui, -apple-system, sans-serif",
      background: "transparent",
      // Same soft glow as the result page's TrendChart, so every line
      // chart in the app reads as one family.
      dropShadow: {
        enabled: true,
        blur: 8,
        opacity: isDark ? 0.4 : 0.25,
        top: 0,
        left: 0,
      },
    },
    theme: { mode: isDark ? "dark" : "light" },
    colors: seriesData.map((s) => s.palette.hex),
    stroke: { curve: "smooth", width: 2.5 },
    xaxis: {
      type: "datetime",
      labels: {
        style: { fontSize: "10px", colors: labelColor },
        datetimeUTC: true,
        // See TrendChart.tsx for the rationale: "MMM yy" duplicated
        // labels when several ticks landed inside the same month
        // (e.g. three "Feb 26" in a row). Day precision makes every
        // tick unique for weekly data. Localized via a formatter -
        // ApexCharts' built-in "d MMM" is English-only.
        formatter: (value: string) => formatMonthDayShort(Number(value), locale),
        hideOverlappingLabels: true,
      },
      tickAmount: 6,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { fontSize: "10px", colors: labelColor },
        formatter: (v: number) => `${Math.round(v)}`,
      },
      title: { text: "AZN/m²", style: { fontSize: "10px", color: labelColor } },
    },
    grid: { borderColor: gridColor, strokeDashArray: 4 },
    markers: { size: 0, hover: { size: 5 } },
    tooltip: {
      theme: isDark ? "dark" : "light",
      x: { formatter: (value: number) => formatDateLong(Number(value), locale) },
      y: {
        formatter: (v: number) => `${Math.round(v)} AZN/m²`,
      },
    },
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "11px",
      itemMargin: { horizontal: 8, vertical: 2 },
      labels: { colors: labelColor },
      markers: { size: 6 },
    },
  };

  const series = seriesData.map((s) => ({
    name: s.district + (s.listingIndices.length > 1
      ? ` (${s.listingIndices.map((i) => `#${i + 1}`).join(", ")})`
      : ""),
    data: s.points,
  }));

  return (
    <section className="h-full rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <h2 className="mb-3 text-sm font-semibold text-gray-900">
        {t("compare.chart.trend_title")}
      </h2>
      <Chart options={options} series={series} type="line" height={280} />
    </section>
  );
}
